/**
 * BodyDetectionConfigPanel — iter110c
 *
 * Admin-UI für die feingranulare Nudity-Region-Detection:
 * - Toggles: blur_nipple / blur_breast / blur_genital
 * - Slider: blur_strength (10..80), confidence_threshold (0..0.95)
 * - Vorher/Nachher Preview mit Test-Bild-Upload
 *
 * Struktur bewusst nicht rounded (Admin-Bereich bleibt eckig/funktional).
 */
import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { Loader2, Upload, RefreshCw, Save, AlertCircle } from "lucide-react";

const REGION_LABELS = {
  nipple:  { label: "Nippel (weiblich)",  desc: "Freigelegte weibliche Nippel — starker Blur empfohlen." },
  breast:  { label: "Brust (weiblich)",   desc: "Weibliche Brust ohne sichtbaren Nippel. Default: aus." },
  genital: { label: "Genital",            desc: "Freigelegter Genitalbereich (alle Geschlechter, immer aktiv wenn an)." },
};

export default function BodyDetectionConfigPanel() {
  const [config, setConfig] = useState(null);
  const [defaults, setDefaults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [preview, setPreview] = useState(null);        // {before_png_b64, after_png_b64, regions_detected, meta}
  const [uploadedName, setUploadedName] = useState("");
  const [previewing, setPreviewing] = useState(false);
  const [reapplying, setReapplying] = useState(false);
  // Zwischenspeicher für Re-Apply ohne Gemini-Call
  const [cachedImageB64, setCachedImageB64] = useState("");
  const [cachedRegions, setCachedRegions] = useState([]);

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const { data } = await api.get("/admin/body-detection/config");
      setConfig({ ...data.config });
      setDefaults(data.defaults);
    } catch (e) {
      setError(e?.response?.data?.detail || "Config-Load fehlgeschlagen.");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function save() {
    setError(""); setSuccess(""); setSaving(true);
    try {
      const values = {
        blur_nipple:  !!config.blur_nipple,
        blur_breast:  !!config.blur_breast,
        blur_genital: !!config.blur_genital,
        blur_male_nipple: !!config.blur_male_nipple,
        stx_full_blur_mode: !!config.stx_full_blur_mode,
        blur_strength: Number(config.blur_strength),
        blur_field_expansion: Number(config.blur_field_expansion),
        confidence_threshold: Number(config.confidence_threshold),
      };
      const { data } = await api.put("/admin/body-detection/config", { values });
      setConfig({ ...data.config });
      setSuccess("Gespeichert. Blur-Regeln aktiv.");
      setTimeout(() => setSuccess(""), 3000);
    } catch (e) {
      setError(e?.response?.data?.detail || "Speichern fehlgeschlagen.");
    } finally { setSaving(false); }
  }

  function reset() {
    if (!defaults) return;
    setConfig({ ...defaults });
    setSuccess("");
  }

  async function onUpload(evt) {
    const file = evt.target.files?.[0];
    if (!file) return;
    setError(""); setPreviewing(true); setPreview(null); setUploadedName(file.name);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/admin/body-detection/preview", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 60000,
      });
      setPreview(data);
      // Zwischenspeicher für Refresh — Original-Bytes (before) + erkannte Regionen
      setCachedImageB64(data.before_png_b64 || "");
      setCachedRegions(data.regions_detected || []);
    } catch (e) {
      setError(e?.response?.data?.detail || "Preview fehlgeschlagen.");
    } finally { setPreviewing(false); }
  }

  async function refreshPreview() {
    if (!cachedImageB64) return;
    setError(""); setReapplying(true);
    try {
      const { data } = await api.post("/admin/body-detection/reapply", {
        image_b64: cachedImageB64,
        regions: cachedRegions,
        config_override: {
          blur_nipple:  !!config.blur_nipple,
          blur_breast:  !!config.blur_breast,
          blur_genital: !!config.blur_genital,
          blur_male_nipple: !!config.blur_male_nipple,
        stx_full_blur_mode: !!config.stx_full_blur_mode,
          blur_strength: Number(config.blur_strength),
          blur_field_expansion: Number(config.blur_field_expansion),
          confidence_threshold: Number(config.confidence_threshold),
        },
      }, { timeout: 20000 });
      // Behalte before, ersetze after + meta
      setPreview((prev) => ({
        before_png_b64: prev?.before_png_b64 || cachedImageB64,
        after_png_b64: data.after_png_b64,
        regions_detected: data.regions_detected,
        meta: data.meta,
        config: data.config,
      }));
    } catch (e) {
      setError(e?.response?.data?.detail || "Refresh fehlgeschlagen.");
    } finally { setReapplying(false); }
  }

  if (loading || !config) {
    return (
      <div className="flex items-center gap-2 text-sm text-[#666]">
        <Loader2 size={14} className="animate-spin" /> Lade Body-Detection-Config…
      </div>
    );
  }

  return (
    <div data-testid="body-detection-config-panel" className="space-y-5 mb-6 pb-6 border-b border-[#E5E5E5]">
      <div>
        <p className="text-sm font-semibold text-[#0A0A0A]">Region-basierte Blur-Konfiguration</p>
        <p className="text-xs text-[#666] mt-0.5">
          Statt das ganze Bild zu blurren, werden nur die ausgewählten Regionen (Nippel/Brust/Genital) anonymisiert.
          Erkennung erfolgt lokal via NudeNet (kein Dritt-Anbieter, JMStV-konform § 4). Anus/Genital fallen unter „Genital".
        </p>
      </div>

      {error && (
        <div data-testid="body-detection-error" className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 text-xs text-red-700">
          <AlertCircle size={13} className="mt-0.5 shrink-0" /> {error}
        </div>
      )}
      {success && (
        <div data-testid="body-detection-success" className="p-3 bg-green-50 border border-green-200 text-xs text-green-700">
          {success}
        </div>
      )}

      {/* Region toggles */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        {(["nipple", "breast", "genital"]).map((k) => {
          const flag = `blur_${k}`;
          const info = REGION_LABELS[k];
          return (
            <label key={k} data-testid={`bd-toggle-${k}`}
              className={`flex items-start gap-2 p-3 border cursor-pointer transition-colors ${
                config[flag] ? "border-[#0A0A0A] bg-[#FAFAFA]" : "border-[#E5E5E5] bg-white hover:bg-[#FAFAFA]"
              }`}>
              <input
                type="checkbox"
                checked={!!config[flag]}
                onChange={(e) => setConfig({ ...config, [flag]: e.target.checked })}
                className="mt-0.5 accent-[#0A0A0A]"
              />
              <div className="min-w-0 flex-1">
                <p className="text-xs font-semibold text-[#0A0A0A]">{info.label}</p>
                <p className="text-[10px] text-[#666] leading-snug mt-0.5">{info.desc}</p>
              </div>
            </label>
          );
        })}
      </div>

      {/* Male-Nipple/Breast Override */}
      <label data-testid="bd-toggle-male-nipple"
        className={`flex items-start gap-2 p-3 border cursor-pointer transition-colors ${
          config.blur_male_nipple ? "border-[#0A0A0A] bg-[#FAFAFA]" : "border-[#E5E5E5] bg-white hover:bg-[#FAFAFA]"
        }`}>
        <input
          type="checkbox"
          checked={!!config.blur_male_nipple}
          onChange={(e) => setConfig({ ...config, blur_male_nipple: e.target.checked })}
          className="mt-0.5 accent-[#0A0A0A]"
        />
        <div className="min-w-0 flex-1">
          <p className="text-xs font-semibold text-[#0A0A0A]">Männliche Nippel / Brust auch blurren</p>
          <p className="text-[10px] text-[#666] leading-snug mt-0.5">
            Default AUS — männliche Nippel/Brust sind kulturell/rechtlich meist unproblematisch.
            Genital wird IMMER geblurrt (unabhängig vom Geschlecht) wenn oben aktiv.
          </p>
        </div>
      </label>

      {/* iter172p · STX Full-Blur-Modus (Owner-Toggle) */}
      <label data-testid="bd-toggle-full-blur-mode"
        className={`flex items-start gap-2 p-3 border cursor-pointer transition-colors ${
          config.stx_full_blur_mode ? "border-[#0A0A0A] bg-[#FAFAFA]" : "border-[#E5E5E5] bg-white hover:bg-[#FAFAFA]"
        }`}>
        <input
          type="checkbox"
          checked={!!config.stx_full_blur_mode}
          onChange={(e) => setConfig({ ...config, stx_full_blur_mode: e.target.checked })}
          className="mt-0.5 accent-[#0A0A0A]"
        />
        <div className="min-w-0 flex-1">
          <p className="text-xs font-semibold text-[#0A0A0A]">STX-Vollbild-Blur für non-AVS User</p>
          <p className="text-[10px] text-[#666] leading-snug mt-0.5">
            AUS = nur die vom Detector erkannten Regionen werden weichgezeichnet (Per-Region).
            AN = das gesamte Bild wird weichgezeichnet (klassisches Verhalten), sobald mindestens
            eine relevante Region erkannt wurde. Wirkt sofort auf allen Frontend-Instanzen.
          </p>
        </div>
      </label>

      {/* Sliders */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div>
          <div className="flex items-baseline justify-between mb-1">
            <label className="text-xs font-medium text-[#0A0A0A]">Blur-Stärke</label>
            <span data-testid="bd-strength-value" className="text-[11px] text-[#666] tabular-nums">
              {config.blur_strength} px
            </span>
          </div>
          <input
            data-testid="bd-strength-slider"
            type="range" min="10" max="80" step="1"
            value={config.blur_strength}
            onChange={(e) => setConfig({ ...config, blur_strength: Number(e.target.value) })}
            className="w-full accent-[#0A0A0A]"
          />
          <p className="text-[10px] text-[#999] mt-0.5">Höhere Werte = stärkere Unkenntlichmachung (weichgezeichnet).</p>
        </div>
        <div>
          <div className="flex items-baseline justify-between mb-1">
            <label className="text-xs font-medium text-[#0A0A0A]">Blur-Feld-Größe</label>
            <span data-testid="bd-expansion-value" className="text-[11px] text-[#666] tabular-nums">
              {config.blur_field_expansion} %
            </span>
          </div>
          <input
            data-testid="bd-expansion-slider"
            type="range" min="0" max="20" step="1"
            value={config.blur_field_expansion}
            onChange={(e) => setConfig({ ...config, blur_field_expansion: Number(e.target.value) })}
            className="w-full accent-[#0A0A0A]"
          />
          <p className="text-[10px] text-[#999] mt-0.5">Padding um die erkannte Region (0 % = strikt, 20 % = großzügig).</p>
        </div>
        <div>
          <div className="flex items-baseline justify-between mb-1">
            <label className="text-xs font-medium text-[#0A0A0A]">Konfidenz-Schwelle</label>
            <span data-testid="bd-threshold-value" className="text-[11px] text-[#666] tabular-nums">
              {(config.confidence_threshold * 100).toFixed(0)} %
            </span>
          </div>
          <input
            data-testid="bd-threshold-slider"
            type="range" min="0" max="95" step="5"
            value={Math.round(config.confidence_threshold * 100)}
            onChange={(e) => setConfig({ ...config, confidence_threshold: Number(e.target.value) / 100 })}
            className="w-full accent-[#0A0A0A]"
          />
          <p className="text-[10px] text-[#999] mt-0.5">Nur Regionen ≥ Schwelle werden geblurrt (Default 55 %).</p>
        </div>
      </div>

      <div className="flex gap-2">
        <button
          data-testid="bd-save"
          onClick={save}
          disabled={saving}
          className="h-10 px-4 bg-[#0A0A0A] text-white text-sm font-medium flex items-center gap-1.5 disabled:opacity-40"
        >
          {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />} Speichern
        </button>
        <button
          data-testid="bd-reset"
          onClick={reset}
          disabled={saving}
          className="h-10 px-3 border border-[#E5E5E5] text-xs text-[#666] flex items-center gap-1 hover:bg-[#F5F5F5]"
        >
          <RefreshCw size={11} /> Defaults
        </button>
      </div>

      {/* Vorher/Nachher Live-Preview */}
      <div className="pt-4 border-t border-[#E5E5E5]">
        <p className="text-sm font-semibold text-[#0A0A0A]">Vorher / Nachher · Live-Preview</p>
        <p className="text-[11px] text-[#666] mt-0.5 mb-3">
          Testbild hochladen (JPEG / PNG / WEBP, max 8 MB). Regionen werden mit AKTUELL GESPEICHERTER Config geblurrt.
        </p>
        <label data-testid="bd-upload-label"
          className="flex items-center justify-center h-24 border-2 border-dashed border-[#E5E5E5] bg-[#FAFAFA] cursor-pointer hover:bg-[#F5F5F5] transition-colors">
          <div className="flex items-center gap-2 text-xs text-[#666]">
            {previewing ? <><Loader2 size={14} className="animate-spin" /> Analysiere Bild …</>
                        : <><Upload size={14} /> Testbild auswählen{uploadedName ? ` · ${uploadedName}` : ""}</>}
          </div>
          <input
            data-testid="bd-upload-input"
            type="file"
            accept="image/jpeg,image/png,image/webp"
            className="hidden"
            onChange={onUpload}
          />
        </label>

        {preview && (
          <div data-testid="bd-preview-result" className="mt-4 space-y-3">
            {/* Refresh-Button — wendet aktuelle Slider/Toggle-Werte ohne Gemini-Call neu an */}
            <div className="flex items-center justify-between">
              <p className="text-[10px] text-[#666]">
                Schieber verändern → Refresh klicken, um das aktuelle Bild neu zu blurren (spart Gemini-API-Calls).
              </p>
              <button
                data-testid="bd-refresh-preview"
                onClick={refreshPreview}
                disabled={reapplying || previewing}
                className="h-8 px-3 border border-[#0A0A0A] text-[#0A0A0A] text-xs font-medium flex items-center gap-1.5 hover:bg-[#0A0A0A] hover:text-white transition-colors disabled:opacity-40"
                title="Aktuelles Bild mit aktuellen Slider-Werten erneut blurren"
              >
                {reapplying ? <><Loader2 size={11} className="animate-spin" /> Wende an …</>
                            : <><RefreshCw size={11} /> Refresh Preview</>}
              </button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <p className="text-[10px] font-bold uppercase tracking-wider text-[#999] mb-1">Vorher</p>
                <img
                  data-testid="bd-preview-before"
                  src={`data:image/png;base64,${preview.before_png_b64}`}
                  alt="Vorher"
                  className="w-full border border-[#E5E5E5]"
                />
              </div>
              <div>
                <p className="text-[10px] font-bold uppercase tracking-wider text-[#999] mb-1">Nachher (geblurrt)</p>
                <img
                  data-testid="bd-preview-after"
                  src={`data:image/png;base64,${preview.after_png_b64}`}
                  alt="Nachher"
                  className="w-full border border-[#0A0A0A]"
                />
              </div>
            </div>
            <div>
              <p className="text-[10px] font-bold uppercase tracking-wider text-[#999] mb-1">
                Erkannte Regionen ({(preview.regions_detected || []).length})
              </p>
              {(preview.regions_detected || []).length === 0 ? (
                <p data-testid="bd-preview-empty" className="text-[11px] text-[#666] italic">Keine relevanten Regionen erkannt.</p>
              ) : (
                <ul data-testid="bd-preview-regions" className="text-[11px] space-y-1">
                  {preview.regions_detected.map((r, i) => (
                    <li key={i} className="flex items-center gap-2 border border-[#E5E5E5] px-2 py-1 bg-white">
                      <span className="uppercase text-[9px] tracking-wider font-bold text-[#0A0A0A]">{r.part}</span>
                      {r.gender && (
                        <span className={`uppercase text-[9px] tracking-wider px-1.5 py-0.5 ${
                          r.gender === "female" ? "bg-pink-50 text-pink-700 border border-pink-200"
                          : r.gender === "male" ? "bg-blue-50 text-blue-700 border border-blue-200"
                          : "bg-[#F5F5F5] text-[#666] border border-[#E5E5E5]"
                        }`}>{r.gender}</span>
                      )}
                      <span className="tabular-nums text-[#666]">
                        Box: {r.box.map((v) => v.toFixed(2)).join(" · ")}
                      </span>
                      <span className="ml-auto tabular-nums text-[#999]">Konfidenz: {(r.confidence * 100).toFixed(0)}%</span>
                    </li>
                  ))}
                </ul>
              )}
              {preview.meta?.regions_applied?.length !== undefined && (
                <p className="text-[10px] text-[#999] mt-2">
                  Blur angewendet auf {preview.meta.regions_applied.length} · übersprungen {preview.meta.regions_skipped?.length || 0} (Schwelle/deaktiviert).
                </p>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
